import { CircleNotchIcon } from "@phosphor-icons/react";
import { Dialog, DialogContent } from "@reactive-resume/ui";
import type { ResumeData } from "@reactive-resume/schema";
import type { Template } from "@reactive-resume/utils";
import { useState } from "react";
import { useNavigate } from "react-router";

import { useToast } from "@/client/hooks/use-toast";
import { extractLayoutFromFile, type ExtractionProgress } from "@/client/services/ocr/extract";
import { terminateOcrWorker } from "@/client/services/ocr/tesseract";
import {
  useAnalyzeResumeLayout,
  useFinalizeResumeImport,
} from "@/client/services/resume/import-flow";
import { useDialog } from "@/client/stores/dialog";

import { ImportFlowShell } from "./components/import-flow-shell";
import { DetectedSectionsStep } from "./steps/detected-sections-step";
import { DocumentUploadStep } from "./steps/document-upload-step";
import { ImportTemplateStep } from "./steps/import-template-step";
import type { ImportFlowStep } from "./types";

const ASSETS = {
  upload: "/images/user-space/onboarding/import-document/import-guide-upload.webp",
  detected: "/images/user-space/onboarding/import-document/import-guide-sections.webp",
  templates: "/images/user-space/onboarding/import-document/guide-template.webp",
};

export const ImportDocumentDialog = () => {
  const { isOpen, close } = useDialog("import-ocr");
  const navigate = useNavigate();
  const { toast } = useToast();
  const analyze = useAnalyzeResumeLayout();
  const finalize = useFinalizeResumeImport();

  const [step, setStep] = useState<ImportFlowStep>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [data, setData] = useState<ResumeData | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [progress, setProgress] = useState<ExtractionProgress>({
    stage: "reading",
    progress: 0,
    label: "",
  });

  const reset = () => {
    setStep("upload");
    setFile(null);
    setData(null);
    setSelectedTemplate(null);
    setProgress({ stage: "reading", progress: 0, label: "" });
    analyze.reset();
    finalize.reset();
  };

  const handleClose = () => {
    void terminateOcrWorker();
    reset();
    close();
  };

  const processFile = async (nextFile: File) => {
    setFile(nextFile);
    setStep("working");
    try {
      const extraction = await extractLayoutFromFile(nextFile, setProgress);
      setProgress({ stage: "structuring", progress: 0.96, label: "Organisation des sections…" });
      const analyzedData = await analyze.mutateAsync(extraction.pages);
      setData(analyzedData);
      setStep("detected");
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data
          ?.message ??
        (error as Error).message ??
        "L’analyse du document a échoué.";
      toast({ variant: "destructive", title: "Import impossible", description: message });
      setStep("upload");
      setFile(null);
    }
  };

  const createResume = async () => {
    if (!file || !data || !selectedTemplate) return;
    try {
      const resume = await finalize.mutateAsync({
        title: file.name.replace(/\.[^.]+$/, "").slice(0, 80),
        template: selectedTemplate,
        data,
      });
      handleClose();
      await navigate(`/client/builder/${resume.id}`);
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data
          ?.message ??
        (error as Error).message ??
        "La création du CV a échoué.";
      toast({ variant: "destructive", title: "Création impossible", description: message });
    }
  };

  const illustration =
    step === "detected" ? ASSETS.detected : step === "templates" ? ASSETS.templates : ASSETS.upload;
  const message =
    step === "detected"
      ? "J’ai retrouvé les sections de votre CV."
      : step === "templates"
        ? "Choisissez maintenant votre modèle !"
        : "Déposez votre CV, je m’occupe du reste.";

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent className="h-[100dvh] w-screen max-w-none overflow-hidden rounded-none border-0 p-0 sm:h-[min(94dvh,900px)] sm:w-[calc(100vw-2rem)] sm:rounded-[1.75rem] lg:max-w-[1500px] [&>button]:hidden">
        <ImportFlowShell illustration={illustration} message={message} onClose={handleClose}>
          {step === "upload" && (
            <DocumentUploadStep
              busy={false}
              onFile={(selected) => void processFile(selected)}
              onBack={handleClose}
            />
          )}
          {step === "working" && (
            <div className="grid min-h-[55dvh] place-items-center text-center">
              <div className="w-full max-w-lg">
                <CircleNotchIcon
                  aria-hidden
                  size={52}
                  className="mx-auto animate-spin text-indigo-600"
                />
                <h1 className="mt-5 text-2xl font-bold text-indigo-950">Analyse de votre CV</h1>
                <p className="mt-2 text-sm text-slate-600">
                  {progress.label || "Préparation du document…"}
                </p>
                <div className="mt-5 h-2 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-indigo-600 to-emerald-500 transition-all"
                    style={{ width: `${Math.max(5, Math.round(progress.progress * 100))}%` }}
                  />
                </div>
              </div>
            </div>
          )}
          {step === "detected" && file && data && (
            <DetectedSectionsStep
              file={file}
              data={data}
              onBack={() => setStep("upload")}
              onChangeFile={() => {
                setFile(null);
                setData(null);
                setStep("upload");
              }}
              onContinue={() => setStep("templates")}
            />
          )}
          {step === "templates" && (
            <ImportTemplateStep
              selected={selectedTemplate}
              busy={finalize.isPending}
              onSelect={setSelectedTemplate}
              onBack={() => setStep("detected")}
              onUse={() => void createResume()}
            />
          )}
        </ImportFlowShell>
      </DialogContent>
    </Dialog>
  );
};
